import React from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';
import { isValidBrandColor } from '../../theme/buildBrandPalette';
import { buildAdornmentTextFieldSx, fieldLabelSx } from '../common/formFieldStyles';

const swatchInputSx = {
  width: 48,
  height: 48,
  p: 0,
  border: `2px solid ${BRAND.line}`,
  borderRadius: BRAND_RADIUS.button,
  cursor: 'pointer',
  background: 'none',
  flexShrink: 0,
  overflow: 'hidden',
  '&::-webkit-color-swatch-wrapper': { p: 0 },
  '&::-webkit-color-swatch': { border: 'none', borderRadius: '6px' },
  '&::-moz-color-swatch': { border: 'none', borderRadius: '6px' },
  '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
};

/** One brand colour role: native swatch picker + hex input + "use default". */
const BrandColorField = ({ id, role, value, defaultColor, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const trimmed = (value || '').trim();
  const hasError = trimmed !== '' && !isValidBrandColor(trimmed);
  const effectiveColor = isValidBrandColor(trimmed) ? trimmed : defaultColor;
  const roleLabel = t(`stores.branding.roles.${role}.label`);

  const handleTextChange = (event) => {
    const next = event.target.value.replace(/[^#0-9a-fA-F]/g, '').slice(0, 7);
    onChange(next && !next.startsWith('#') ? `#${next}` : next);
  };

  return (
    <Box id={id}>
      <Typography component="label" htmlFor={`${id}-hex`} sx={{ ...fieldLabelSx, display: 'block', mb: 0.25 }}>
        {roleLabel}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(`stores.branding.roles.${role}.help`)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box
          component="input"
          type="color"
          id={`${id}-swatch`}
          aria-label={t('stores.branding.pickColor', { role: roleLabel })}
          value={effectiveColor.toLowerCase()}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          sx={swatchInputSx}
        />
        <TextField
          id={`${id}-hex`}
          value={value || ''}
          onChange={handleTextChange}
          placeholder={defaultColor}
          disabled={disabled}
          error={hasError}
          helperText={hasError ? t('stores.branding.invalidHex') : t('stores.branding.defaultValue', { color: defaultColor })}
          inputProps={{ spellCheck: false, autoComplete: 'off', maxLength: 7 }}
          InputProps={{
            endAdornment: trimmed ? (
              <InputAdornment position="end">
                <Button
                  id={`${id}-use-default`}
                  size="small"
                  disabled={disabled}
                  onClick={() => onChange('')}
                  sx={{ color: BRAND.accentText, fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  {t('stores.branding.useDefault')}
                </Button>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            flex: 1,
            ...buildAdornmentTextFieldSx(hasError),
            '& .MuiInputAdornment-root': { backgroundColor: 'transparent', pl: 0.5, pr: 0.5 },
            '& .MuiOutlinedInput-input': { padding: '12px 16px', fontSize: '16px', textTransform: 'uppercase' },
          }}
        />
      </Box>
    </Box>
  );
};

export default BrandColorField;
